/* eslint-disable max-len */
import React, { useState, useEffect, useContext, useRef } from "react";
import FullCalendar from "@fullcalendar/react";
import dayGridPlugin from "@fullcalendar/daygrid";
import interactionPlugin from "@fullcalendar/interaction";
import GlobalContext from "../../components/Context/GloablContext";
import { dataService } from "../../services/data.services";
import AddHoliday from "./AddHoliday";
import BranchHolidayList from "./BranchHolidayList";
import FeeStructureDropdown from "../../components/FeeStructureDropdown";
import Toast from "../../components/Toast";
import Loader from "../../components/Loader";
import { GetBranchId } from '../../CommonMethods/GetBranchId';
import {
    addOneDay,
    formatDateLabel,
    formatDayLabel,
    normalizeHolidayItems,
    getHolidayTypeLabel,
    isHolidayPast,
    toLocalStart,
    ymd,
} from "./CommonMethods";

export default function BranchHoliday() {
    const [branchId, setBranchId] = useState(null);
    const [academicYears, setAcademicYears] = useState([]);
    const [selectedAcademicYear, setSelectedAcademicYear] = useState(null);
    const [academicYearLoading, setAcademicYearLoading] = useState(false);
    const [fetchHolidaysLoading, setFetchHolidaysLoading] = useState(false);

    // derived academic year bounds (YYYY-MM-DD strings)
    const [academicStartDate, setAcademicStartDate] = useState(null);
    const [academicEndDate, setAcademicEndDate] = useState(null); // inclusive
    const [appliedAcademicYear, setAppliedAcademicYear] = useState(null);

    const [selectedDate, setSelectedDate] = useState(null);
    const [holidays, setHolidays] = useState([]);
    const [holidayItems, setHolidayItems] = useState([]);
    const [selectedHolidayData, setSelectedHolidayData] = useState(null);
    const [modalInitialScreen, setModalInitialScreen] = useState("view");
    const [viewMode, setViewMode] = useState("calendar");

    const [toastErrorMessage, setToastErrorMessage] = useState("");
    const [toastSuccessMessage, setToastSuccessMessage] = useState("");
    const { isInactiveBranch } = useContext(GlobalContext);

    const tableContainerRef = useRef();
    const calendarRef = useRef(null);
    const initialFetchDoneRef = useRef(false);
    const calendarKey = `${academicStartDate || "no-start"}-${academicEndDate || "no-end"}`;

    const fetchHolidayDetails = async ({ holidayRangeId = null, branchHolidayId = null }) => {
        if (!selectedAcademicYear?._id || !branchId) return null;

        const params = new URLSearchParams({
            branch_id: branchId,
            academic_year_id: selectedAcademicYear?._id || "",
        });

        if (holidayRangeId) {
            params.append("holiday_range_id", holidayRangeId);
        } else if (branchHolidayId) {
            params.append("branch_holiday_id", branchHolidayId);
        } else {
            return null;
        }

        const endpoint = `holidays/view?${params.toString()}`;
        const response = await dataService.GetAPI(endpoint, true);
        if (response?.data?.status !== 200) return null;

        const holidayData = response?.data?.data?.holiday || null;
        if (!holidayData) return null;
        holidayData.holiday_range_id = holidayRangeId;
        holidayData.branch_holiday_id = branchHolidayId;
        return holidayData;
    };

    const buildHolidayDataFromItem = (item) => {
        const holiday = item?.holiday;
        if (!holiday) return null;

        return {
            ...holiday,
            start_date: item?.start_date || item?.date || holiday?.start_date || null,
            end_date: item?.end_date || item?.date || holiday?.end_date || null,
            holiday_range_id: item?.date_range_id || holiday?.holiday_range_id || null,
            branch_holiday_id: holiday?.branch_holiday_id || null,
        };
    };

    // ---- Click day on calendar: fetch holiday for that date and open modal ----
    const handleDateClick = async (info) => {
        // ensure academic year selected and clicked date is within AY bounds
        if (!selectedAcademicYear) {
            setToastErrorMessage("Please select Academic Year");
            return;
        }
        const clickedDate = toLocalStart(info.date); // JS Date at local midnight
        // guard: clicked date must be between academicStartDate and academicEndDate (inclusive)
        if (academicStartDate && academicEndDate) {
            const clickedStr = ymd(clickedDate);
            if (clickedStr < academicStartDate || clickedStr > academicEndDate) {
                setToastErrorMessage("Selected date is outside the chosen academic year");
                return;
            }
        }

        setSelectedDate(clickedDate);
        setModalInitialScreen("view");

        try {
            const holidayRangeId = info?.holiday_range_id || info?.event?.extendedProps?.holiday_range_id || null;
            const branchHolidayId = info?.branch_holiday_id || info?.event?.extendedProps?.branch_holiday_id || null;
            setSelectedHolidayData(null);

            if (holidayRangeId || branchHolidayId) {
                const holidayData = await fetchHolidayDetails({ holidayRangeId, branchHolidayId });
                setSelectedHolidayData(holidayData);
            }
        } catch (error) {
            console.error("Error fetching holiday:", error);
            setSelectedHolidayData(null);
            setToastErrorMessage(
                error?.response?.data?.message || "Failed to fetch holiday"
            );
        }

        // Open the modal after we set the state
        setModalOpen(true);
    };

    // ---- Dropdown change handlers ----
    const onAcademicYearChange = (ay) => setSelectedAcademicYear(ay || null);

    // ---- API: Academic Years ----
    const getAcademicYears = async (bId) => {
        if (!bId) return;
        try {
            setAcademicYearLoading(true);
            const endpoint = `academic-years?branch_id=${bId}&filter=true&status=ongoing,upcoming,completed`;
            const response = await dataService.GetAPI(endpoint);
            const temp = response?.data?.data?.data || [];
            setAcademicYears(temp);
            // If no academic year selected yet, default to the first 'ongoing' AY.
            if (!selectedAcademicYear) {
                const ongoing = temp.find((ay) => ay?.status === 'ongoing');
                if (ongoing) {
                    setSelectedAcademicYear(ongoing);
                } else if (temp.length > 0) {
                    // fallback to first available AY if none ongoing
                    setSelectedAcademicYear(temp[0]);
                } else {
                    setSelectedAcademicYear(null);
                }
            }

            return temp;
        } catch (error) {
            console.log(error);
            setToastErrorMessage(
                error?.response?.data?.message || "Something went wrong"
            );
            return [];
        } finally {
            setAcademicYearLoading(false);
        }
    };

    // ---- API: Holidays list for calendar ----
    const getHolidays = async (jumpToStart = false) => {
        const currentAcademicYear = selectedAcademicYear;
        if (!currentAcademicYear?._id) {
            setToastErrorMessage("Please select Academic Year");
            return;
        }

        try {
            setFetchHolidaysLoading(true);
            setAppliedAcademicYear(currentAcademicYear);
            const params = new URLSearchParams({
                branch_id: branchId,
                academic_year_id: currentAcademicYear._id,
            });

            const endpoint = `holidays/list-holidays?${params.toString()}&is_branch_holiday=true`;
            const response = await dataService.GetAPI(endpoint, true);

            if (response?.data?.status === 200) {
                const data = normalizeHolidayItems(response?.data?.data || {}, {
                    idKeys: ["branch_holiday_id", "holiday_range_id"],
                });
                setHolidayItems(data);
                const formatted = data.flatMap((item) =>
                    [{
                        title: item?.holiday?.name,
                        start: item?.date || item?.start_date,
                        end: item?.date ? undefined : addOneDay(item?.end_date),
                        extendedProps: {
                            reason: item?.holiday?.reason || "",
                            holiday_range_id: item?.date_range_id || item?.holiday?.holiday_range_id || null,
                            branch_holiday_id: item?.holiday?.branch_holiday_id || null,
                        },
                        allDay: true,
                    }]
                );
                setHolidays(formatted);
                if (jumpToStart) {
                    const start = currentAcademicYear?.start_date
                        ? currentAcademicYear.start_date.split('T')[0]
                        : null;
                    if (start && calendarRef.current) {
                        calendarRef.current.getApi().gotoDate(start);
                    }
                }
            } else {
                setHolidayItems([]);
                setHolidays([]);
            }
        } catch (error) {
            console.error("Error fetching holidays:", error);
            setHolidayItems([]);
            setHolidays([]);
            setToastErrorMessage(
                error?.response?.data?.message || "Failed to fetch holidays"
            );
        } finally {
            setFetchHolidaysLoading(false);
        }
    };

    // ---- Lifecycle: fetch branchId then academic years ----
    useEffect(() => {
        let mounted = true;
        GetBranchId()
            .then((id) => {
                if (mounted) setBranchId(id);
            })
            .catch((err) => {
                console.error('Failed to get branch id', err);
                if (mounted) setToastErrorMessage("Failed to determine branch");
            });
        return () => { mounted = false; };
    }, []);

    useEffect(() => {
        if (branchId) {
            getAcademicYears(branchId);
        }
    }, [branchId]);

    useEffect(() => {
        if (selectedAcademicYear?._id && !initialFetchDoneRef.current) {
            initialFetchDoneRef.current = true;
            getHolidays();
        }
    }, [selectedAcademicYear?._id]);

    // when appliedAcademicYear changes, derive start/end strings for calendar validRange
    useEffect(() => {
        if (appliedAcademicYear) {
            const start = appliedAcademicYear.start_date
                ? appliedAcademicYear.start_date.split('T')[0]
                : null;
            const end = appliedAcademicYear.end_date
                ? appliedAcademicYear.end_date.split('T')[0]
                : null;
            setAcademicStartDate(start);
            setAcademicEndDate(end);
        } else {
            setAcademicStartDate(null);
            setAcademicEndDate(null);
        }
    }, [appliedAcademicYear]);

    useEffect(() => {
        if (viewMode !== "calendar") return;
        if (!academicStartDate || !calendarRef.current) return;
        calendarRef.current.getApi().gotoDate(academicStartDate);
    }, [viewMode, academicStartDate]);

    useEffect(() => {
        const calendarRoot = calendarRef.current?.elRef?.current;
        if (!calendarRoot) return;

        const calendarToggleButton = calendarRoot.querySelector(".fc-calendarToggle-button");
        const listToggleButton = calendarRoot.querySelector(".fc-listToggle-button");

        const applyButtonState = (button, isActive) => {
            if (!button) return;
            button.classList.add("branch-holiday-toggle-btn");
            button.classList.toggle("branch-holiday-toggle-btn-active", isActive);
        };

        applyButtonState(calendarToggleButton, viewMode === "calendar");
        applyButtonState(listToggleButton, viewMode === "list");
    }, [viewMode, calendarKey]);

    // ---- Modal control ----
    const [modalOpen, setModalOpen] = useState(false);
    const handleAddHolidayButton = () => {
        if (!selectedAcademicYear) {
            setToastErrorMessage("Please select Academic Year");
            return;
        }
        setSelectedDate(null);
        setSelectedHolidayData(null);
        setModalInitialScreen("view");
        setModalOpen(true);
    };

    const openHolidayEditor = (item, screen) => {
        if (screen === "delete" && isHolidayPast(item)) {
            setToastErrorMessage("Past holiday cannot be deleted");
            return;
        }

        const holidayData = buildHolidayDataFromItem(item);
        if (!holidayData) {
            setToastErrorMessage("Failed to load holiday details");
            return;
        }

        setSelectedDate(item?.date || item?.start_date || null);
        setSelectedHolidayData(holidayData);
        setModalInitialScreen(screen);
        setModalOpen(true);
    };

    const handleAddHoliday = (holiday) => {
        if (!selectedAcademicYear) {
            setToastErrorMessage("Please select Academic Year");
            return;
        }
        if (!holiday) return;
        const key = (e) => `${e.title}::${e.start}`;
        setHolidays((prev) => {
            const next = [...prev, holiday];
            const seen = new Set();
            return next.filter((e) => {
                const k = key(e);
                if (seen.has(k)) return false;
                seen.add(k);
                return true;
            });
        });
    };

    return (
        <div className="bg-white" ref={tableContainerRef}>
            {/* Controls row */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 my-2 items-end  border p-3 rounded  sticky top-0  z-20 bg-white">
                {/* Academic Year */}
                <div className="flex flex-col w-[250px]">
                    <label className="text-f7 font-normal text-[#031624] mb-1 inline-flex">
                        Academic Year<span className="req-fld-astrk">*</span>
                    </label>
                    <FeeStructureDropdown
                        dropDownName={"academic_year"}
                        id={"name"}
                        test_id={"year_name"}
                        value={selectedAcademicYear?.name || "Select Academic Year"}
                        placeHolder={"Select Academic Year"}
                        isMandatoryField={true}
                        options={academicYears}
                        handleOnchangeInput={(key, value) => onAcademicYearChange(value)}
                        loading={academicYearLoading}
                        buttonDisabled={academicYearLoading}
                    />
                </div>

                {/* Actions */}
                <div className="flex md:col-span-2 justify-end gap-3">
                    
                    <button
                        type="button"
                        onClick={() => getHolidays(true)}
                        style={{'min-width': '120px'}}
                        disabled={academicYearLoading || fetchHolidaysLoading}
                        className={`view-actn-btn`}
                    >
                        {fetchHolidaysLoading || academicYearLoading ? <Loader /> : "Apply Filter"}
                    </button>

                    {!isInactiveBranch && <button
                        type="button"
                        onClick={handleAddHolidayButton}
                        className="prmy-btn onboardInstitution"
                    >
                        Add Holiday(s)
                    </button>}
                </div>
            </div>
            <div
                className={`border rounded-md p-3 branch-holiday-calendar ${viewMode === "list" ? "branch-holiday-calendar-list-mode" : ""}`}
                style={{ position: 'relative', zIndex: 0 }}
            >
                <div className="branch-holiday-calendar-shell">
                    <FullCalendar
                        key={calendarKey}
                        ref={calendarRef}
                        plugins={[dayGridPlugin, interactionPlugin]}
                        initialView="dayGridMonth"
                        initialDate={academicStartDate || undefined}
                        events={holidays}
                        height={viewMode === "calendar" ? "60vh" : "auto"}
                        headerToolbar={viewMode === "calendar" ? {
                            start: "prev title next",
                            center: "",
                            end: "calendarToggle,listToggle",
                        } : {
                            start: "",
                            center: "",
                            end: "calendarToggle,listToggle",
                        }}
                        customButtons={{
                            calendarToggle: {
                                text: "Calendar",
                                click: () => setViewMode("calendar"),
                            },
                            listToggle: {
                                text: "List",
                                click: () => setViewMode("list"),
                            },
                        }}
                        dayCellClassNames={(arg) => {
                            if (!academicStartDate || !academicEndDate) return [];
                            const cellDateStr = ymd(arg.date);
                            if (cellDateStr < academicStartDate || cellDateStr > academicEndDate) {
                                return ['disabled-academic-day'];
                            }
                            return [];
                        }}
                        dayCellDidMount={(arg) => {
                            if (!academicStartDate || !academicEndDate) return;
                            const cellDateStr = ymd(arg.date);
                            if (cellDateStr < academicStartDate || cellDateStr > academicEndDate) {
                                arg.el.setAttribute('aria-disabled', 'true');
                                arg.el.setAttribute('title', 'Date outside selected academic year');
                            } else {
                                arg.el.removeAttribute('aria-disabled');
                                arg.el.setAttribute('title', 'Click to add holiday');
                            }
                        }}
                        eventClick={(info) => {
                            handleDateClick({
                                date: info.event.start,
                                holiday_range_id: info?.event?.extendedProps?.holiday_range_id || null,
                                branch_holiday_id: info?.event?.extendedProps?.branch_holiday_id || null,
                            });
                        }}
                        eventDidMount={(info) => {
                            if (info && info.el) info.el.style.cursor = 'pointer';
                        }}
                        dateClick={handleDateClick}
                    />
                </div>
                {viewMode === "list" && (
                    <BranchHolidayList
                        holidays={holidayItems}
                        formatDateLabel={formatDateLabel}
                        formatDayLabel={formatDayLabel}
                        onEdit={(item) => openHolidayEditor(item, "edit")}
                        onDelete={(item) => openHolidayEditor(item, "delete")}
                        getHolidayTypeLabel={getHolidayTypeLabel}
                        isInactiveBranch={isInactiveBranch}
                    />
                )}
            </div>

            {/* Modal */}
            <AddHoliday
                academicYear={selectedAcademicYear}
                holidayScope="BRANCH"
                setToastmessage={setToastSuccessMessage}
                setToastErrorMessage={setToastErrorMessage}
                open={modalOpen}
                branch_id={branchId}
                onClose={() => {
                    setModalOpen(false);
                    setSelectedDate(null);
                    setSelectedHolidayData(null);
                    setModalInitialScreen("view");
                }}
                getHolidays={getHolidays}
                onSave={handleAddHoliday}
                selectedDate={selectedDate}
                holidayData={selectedHolidayData}
                initialEditModeScreen={modalInitialScreen}
            />

            {!modalOpen && toastErrorMessage !== '' && (
                <Toast
                    message={toastErrorMessage}
                    type="error"
                    setToastmessage={setToastErrorMessage}
                />
            )}

            {!modalOpen && toastSuccessMessage !== '' && (
                <Toast
                    message={toastSuccessMessage}
                    type="success"
                    setToastmessage={setToastSuccessMessage}
                />
            )}
        </div>
    );
}
